'use client';

import styles from '@/styles/address.module.css';
import { Job } from '@/types/job';
import { cepMask } from '@/utils/cep-mask';
import { dateMask, dateStrISOBrazilToDate, dayMonth, mapWeekDay, timeWithHour12, weekDayObject } from '@/utils/date';
import { useCallback, useMemo, useState } from 'react';
import SimpleModal from '../modals/simple-modal';
import GtagService from '@/services/Gtag.service';

interface Props {
  job: Job;
  open: boolean;
  onClose: () => void;
}

export default function InterviewAddressModal({ job, open, onClose }: Props) {
  const [selectedWeekDay, setSelectedWeekDay] = useState(job.schedule[0].day);

  const interviewDaysOfWeek = useMemo(() => Array.from(new Set(job.schedule.map(({ day }) => day))), [job.schedule]);

  const interviewDays = useMemo(() => {
    return interviewDaysOfWeek.reduce((acc, shortWeekDay) => {
      const indexFirstWeekDay = weekDayObject[shortWeekDay];
      const date = new Date();
      date.setDate(date.getDate());
      while (date.getDay() != indexFirstWeekDay) {
        date.setDate(date.getDate() + 1);
      }
      acc[shortWeekDay] = new Date(date);
      return acc;
    }, {} as any);
  }, [interviewDaysOfWeek]);

  const handleClickWeekDay = useCallback(
    (shortWeekDay: string) => {
      setSelectedWeekDay(shortWeekDay);
      const date = dateMask(interviewDays[shortWeekDay]);

      GtagService.eventCadastroVaga({
        type: job.slug,
        status: `candidatura-logado-assinante-organizador`,
        contact: `Local`,
        date,
      });
    },
    [interviewDays, job.slug],
  );

  return (
    <SimpleModal open={open} onClose={onClose} className={`modal-contact-job`}>
      <div className={styles.container}>
        <h3>Endereço</h3>
        <ul className="modal-contact-job-list">
          <li>
            {job.interviewAddress?.street}, Nº {job.interviewAddress?.number}
          </li>
          <li>Bairro: {job.interviewAddress?.district}</li>
          <li>
            Cidade: {job.city} - Estado: {job.state}
          </li>
          <li>CEP: {cepMask(job.interviewAddress?.zipCode || ``)} </li>
        </ul>
        <h3>Horário</h3>
        <ul>
          {job.schedule
            .filter(({ day }) => day === selectedWeekDay)
            .map(({ day, startAt, endAt }) => (
              <li key={`time-${day}-${startAt}`}>
                Das{` `}
                {timeWithHour12(dateStrISOBrazilToDate(startAt))}
                {` `}às{` `}
                {timeWithHour12(dateStrISOBrazilToDate(endAt))}
              </li>
            ))}
        </ul>
        <h3>Dias disponíveis</h3>
        <ul className="modal-contact-job-days">
          {interviewDaysOfWeek.map((shortWeekDay) => {
            const isSelected = selectedWeekDay === shortWeekDay;
            const day = dayMonth(interviewDays[shortWeekDay]);
            const weekDay = mapWeekDay(shortWeekDay);

            return (
              <li className={isSelected ? `selected` : ``} key={`week-day-${shortWeekDay}-${job.id}`}>
                <button
                  type="button"
                  className="modal-contact-job-day-button"
                  aria-pressed={isSelected}
                  aria-label={`${weekDay}, ${day}`}
                  onClick={() => handleClickWeekDay(shortWeekDay)}
                >
                  <span>{day}</span>
                  <span>{weekDay}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {/* <label className="modal-contact-job-check-calendar">
            Marcar no calendário
            <input type="checkbox" />
            <span className="checkmark"></span>
          </label> */}
        {job?.interviewAddress?.note && (
          <>
            <h3>Observação:</h3>
            <ul className="modal-contact-job-list">
              <li>{job.interviewAddress?.note}</li>
            </ul>
          </>
        )}
        <div className="d-flex align-items-center justify-content-center">
          <button className={styles.btn_submit} onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </SimpleModal>
  );
}
